import {ControlButton, TscButtonType, TscStyles, TscThemeName, TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import React, {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import Constants from '../../constants/Constants';
import cssConstants from '../../constants/cssConstants';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {mediaStore} from '../../context/mediaProvider';
import {themeStore} from '../../context/themeProvider';
import Analytics from '../../service/analytics';
import SnagitLogoSvg from '../../static/svg/SnagitLogo.svg';
import {setupCSSHeightObserver} from '../../util/CSSHeightObserver';

const MarketingPane = styled.div<{theme: TscThemeNames}>`
   box-shadow: 0 3px 6px rgba(0,0,0,.15), 0 0 2px rgba(0,0,0,.1);
   width: 100%;
   font-family: sans-serif;
   display: flex;
   background-color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.medium : TscStyles.color.ui.dawn.light};
   border-radius: 4px;
   -webkit-box-align: center;
   align-items: center;
   align-content: flex-end;
   height: 2.5rem;
   margin-bottom: .75rem;

   @media screen and (min-width: ${cssConstants.snagitMarketingPaneBreakpoint}px) {
      height: 3.5rem;
   }

   @media screen and (max-width: ${cssConstants.conversationsBreakpoint}px) {
      margin-bottom: .5rem;
   }
`;

const MarketingContainer = styled.span<{theme: TscThemeNames}>`
   display: flex;
   flex: 1 0 auto;
   -webkit-box-align: center;
   align-items: center;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
`;

const ButtonContainer = styled.div`
   display: flex;
   flex: 0 0 auto;
   align-items: center;
   -webkit-box-align: center;
   margin-left: .5rem;
`;

const IconSvg = styled.span<{theme: TscThemeNames}>`
   margin: .5rem .5rem .5rem .5rem;
   display:flex;

   svg {
      height: 24px;
      width: 24px;
   }

   @media screen and (min-width: ${cssConstants.snagitMarketingPaneBreakpoint}px) {
      margin: .5rem .5rem .5rem 1rem;
      svg {
         height: 32px;
         width: 32px;
      }
   }
`;

const IconText = styled.span`
   display: inline-block;
   margin-right: .5rem;
   font-weight: 600;
   font-size: 1rem;
   font-family: "Proxima Nova", proxima-nova, sans-serif;

   @media screen and (min-width: ${cssConstants.snagitMarketingPaneBreakpoint}px) {
      font-size: 1.125rem;
      margin-right: 1.25rem;
   }
`;

const DetailText = styled.span`
   display: none;
   font-family: "Proxima Nova", proxima-nova, sans-serif;
   margin-top: .125rem;
   font-size: .8125rem;

   @media screen and (min-width: ${cssConstants.snagitMarketingPaneShowDetailTextBreakpoint}px) {
      display:inline;
   }

   @media screen and (min-width: ${cssConstants.snagitMarketingPaneBreakpoint}px) {
      font-size: .875rem;
   }
`;

const CtaButtonSpan = styled.span`
   margin: 0 .5rem 0 0;

   @media screen and (min-width: ${cssConstants.snagitMarketingPaneBreakpoint}px) {
      margin: 1rem;
   }
`;

export const MarketingPaneBase: React.FC<IStateMappedProps> = ({Hash, theme}) => {
   const marketingPaneRef = React.createRef<HTMLDivElement>();

   const {t} = useTranslation();

   const recordGABannerButtonClickedEvent = () => {
      const gaViewEvent = {
         category: Constants.analytics.promotionsCategory,
         action: Constants.analytics.madeWithSnagitBannerButtonClicked,
         label: Hash
      };
      Analytics.gaEvent(gaViewEvent);
   };

   const recordGAMarketingPaneDisplayedEvent = () => {
      const gaViewEvent = {
         category: Constants.analytics.promotionsCategory,
         action: Constants.analytics.madeWithSnagitBannerDisplayed,
         label: Hash
      };
      Analytics.gaEvent(gaViewEvent);
   };

   const navigateToMarketingPage = () => {
      recordGABannerButtonClickedEvent();
      window.open(
         Constants.navigation.screencast.madeWithSnagitMarketingLink,
         '_blank'
      );
   };

   useEffect(() => {
      recordGAMarketingPaneDisplayedEvent();
   }, []);

   setupCSSHeightObserver(marketingPaneRef, cssConstants.variables.marketingBannerHeight);

   return (
      <MarketingPane theme={theme} ref={marketingPaneRef}>
         <MarketingContainer theme={theme}>
            <IconSvg>
               <SnagitLogoSvg/>
            </IconSvg>
            <IconText>
               {t('marketing.madeWithSnagit')}
            </IconText>
            <DetailText> {t('marketing.madeWithSnagitDetailText')}</DetailText>
         </MarketingContainer>
         <ButtonContainer>
            <CtaButtonSpan>
               <ControlButton
                  themeName={theme}
                  buttonType={TscButtonType.primary}
                  testId="marketing-call-to-action"
                  label={t('marketing.trySnagitForFree')}
                  onClick={navigateToMarketingPage}
               />
            </CtaButtonSpan>
         </ButtonContainer>
      </MarketingPane>);
};

export interface IStateMappedProps {
   theme: TscThemeNames;
   Hash: string;
}

const mapStatesToProps = (
   // eslint-disable-next-line no-empty-pattern
   {},
   {theme}: Partial<IStateMappedProps>,
   {Hash}: Partial<IStateMappedProps>
): IStateMappedProps => ({Hash, theme});

export default withMemoizedContexts(mapStatesToProps, themeStore, mediaStore)(MarketingPaneBase);
